/**
 * Vuex Store Module for SignalR State Management
 */
import signalRService from '@/services/signalr-service';

const signalrModule = {
  namespaced: true,
  
  state: () => ({
    isConnected: false,
    isConnecting: false,
    connectionId: null,
    connectionError: null,
    lastEvent: null,
  }),

  mutations: {
    SET_CONNECTED(state, isConnected) {
      state.isConnected = isConnected;
    },
    SET_CONNECTING(state, isConnecting) {
      state.isConnecting = isConnecting;
    },
    SET_CONNECTION_ID(state, connectionId) {
      state.connectionId = connectionId;
    },
    SET_CONNECTION_ERROR(state, error) {
      state.connectionError = error;
    },
    SET_LAST_EVENT(state, event) {
      state.lastEvent = event;
    },
    RESET_STATE(state) {
      state.isConnected = false;
      state.isConnecting = false;
      state.connectionId = null;
      state.connectionError = null;
      state.lastEvent = null;
    }
  },

  actions: {
    /**
     * Connect to SignalR hub
     * @param {Object} context - Vuex context
     * @param {Object} credentials - { username, roleId }
     */
    async connect({ commit, state }, { username, roleId }) {
      // Prevent multiple connection attempts
      if (state.isConnected || state.isConnecting) {
        console.log('SignalR already connected or connecting');
        return signalRService.connection;
      }

      commit('SET_CONNECTING', true);
      commit('SET_CONNECTION_ERROR', null);

      try {
        const connection = await signalRService.connect(username, roleId);
        
        commit('SET_CONNECTED', true);
        commit('SET_CONNECTION_ID', connection.connectionId);
        commit('SET_CONNECTING', false);
        
        // Register lifecycle event handlers
        signalRService.on('_connectionClosed', 'vuex', (error) => {
          commit('SET_CONNECTED', false);
          commit('SET_CONNECTION_ID', null);
          if (error) {
            commit('SET_CONNECTION_ERROR', error.message || 'Connection closed');
          }
        });

        signalRService.on('_reconnecting', 'vuex', () => {
          commit('SET_CONNECTING', true);
          commit('SET_CONNECTED', false);
        });

        signalRService.on('_reconnected', 'vuex', (connectionId) => {
          commit('SET_CONNECTED', true);
          commit('SET_CONNECTION_ID', connectionId);
          commit('SET_CONNECTING', false);
          commit('SET_CONNECTION_ERROR', null);
        });

        return connection;
      } catch (error) {
        commit('SET_CONNECTING', false);
        commit('SET_CONNECTED', false);
        commit('SET_CONNECTION_ERROR', error.message || 'Failed to connect');
        throw error;
      }
    },

    /**
     * Disconnect from SignalR hub
     */
    async disconnect({ commit }) {
      await signalRService.disconnect();
      commit('RESET_STATE');
    },

    /**
     * Register event handler
     * @param {Object} context - Vuex context
     * @param {Object} payload - { eventName, handlerId, callback }
     */
    registerHandler({ commit }, { eventName, handlerId, callback }) {
      signalRService.on(eventName, handlerId, (...args) => {
        commit('SET_LAST_EVENT', { eventName, args, timestamp: new Date() });
        callback(...args);
      });
    },

    /**
     * Unregister event handler
     * @param {Object} context - Vuex context
     * @param {Object} payload - { eventName, handlerId }
     */
    unregisterHandler(_, { eventName, handlerId }) {
      signalRService.off(eventName, handlerId);
    },

    /**
     * Unregister all handlers for a component
     * @param {Object} context - Vuex context
     * @param {string} handlerId - Component identifier
     */
    unregisterAllHandlers(_, handlerId) {
      signalRService.offAll(handlerId);
    },

    /**
     * Invoke a method on the hub
     * @param {Object} context - Vuex context
     * @param {Object} payload - { method, args }
     */
    async invoke(_, { method, args = [] }) {
      return await signalRService.invoke(method, ...args);
    }
  },

  getters: {
    isConnected: (state) => state.isConnected,
    isConnecting: (state) => state.isConnecting,
    connectionId: (state) => state.connectionId,
    connectionError: (state) => state.connectionError,
    connectionState: () => signalRService.getState(),
    service: () => signalRService,
  }
};

export default signalrModule;
